/**
 * TaskEventsList - Shared task timeline events component
 *
 * Unified component replacing TaskProcessingFlow and TaskTimelineView.
 * Supports rendering modes via props:
 * - flat: no header/progress, for embedding inside OrderProcessingFlow (uses useTaskTimeline)
 * - compact + showPhases: phase-grouped with limited events per phase (for DriverTasks)
 * - showPhases: full phase-grouped view with template (uses useTaskProcessingFlow)
 * - default: flat list of all recorded events
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  RefreshCw,
  AlertCircle,
  Loader2,
  Clock,
  Check,
  ChevronDown,
  ChevronRight,
  Ship,
  Building2,
} from 'lucide-react';
import { useTaskTimeline } from '../../../hooks/useTaskTimeline';
import { useTaskProcessingFlow } from '../../../hooks/useTaskProcessingFlow';
import {
  getEventIcon,
  getEventTitle,
  getTriggeredByLabel,
  getTriggeredByIcon,
  formatTime,
} from './taskEventHelpers';
import { ProcessingStatusIcon } from './ProcessingStatusIcon';
import type { TaskTimelineEvent } from '../../../types/timeline';
import type { TaskTemplateEvent, TaskPhaseTemplateData } from '../../../types/timeline';
import type { TaskPhase } from '../../../types/timeline';
import type { EventStatus } from '../../../types/orderProcessingFlow';
import { cn } from '../../../lib/utils';
import { socketService } from '../../../services/socket';

interface TaskEventsListProps {
  taskId: number | string;
  flat?: boolean;
  compact?: boolean;
  showPhases?: boolean;
  showProgress?: boolean;
  showTimeSummary?: boolean;
  taskType?: string;
  currentStatus?: string;
  onRefresh?: () => void;
  className?: string;
}

// Max events per phase in compact mode before showing "+N more"
const COMPACT_PHASE_LIMIT = 3;

// ==================== Template Event Row (for showPhases mode) ====================

const TemplateEventRow: React.FC<{
  event: TaskTemplateEvent;
  isLast: boolean;
}> = ({ event, isLast }) => {
  const isPending = event.status === 'pending';
  const iconStatus: EventStatus = event.status === 'completed' ? 'completed' : 'pending';

  return (
    <div className="flex items-start gap-3 relative ml-3">
      {/* Vertical connecting line */}
      {!isLast && (
        <div className="absolute left-[9px] top-7 bottom-0 w-px bg-gray-200" />
      )}

      {/* Status icon */}
      <ProcessingStatusIcon status={iconStatus} size="sm" />

      {/* Content */}
      <div className={cn('flex-1 min-w-0 pb-4', isPending && 'opacity-50')}>
        <p className={cn(
          'text-sm font-medium',
          isPending ? 'text-gray-400' : 'text-gray-800'
        )}>
          {event.event_name}
        </p>
        {event.event_time ? (
          <div className="mt-0.5 text-xs text-gray-400 space-y-0.5">
            {/* Special rendering for assigned event: show dispatcher + assigned driver */}
            {event.template_key === 'assigned' && event.metadata?.driver_name ? (
              <>
                <div className="flex items-center gap-1">
                  <span className="capitalize">{event.metadata?.dispatcher_role || 'dispatcher'}</span>
                  {event.metadata?.dispatcher_name && (
                    <>
                      <span>·</span>
                      <span>{event.metadata.dispatcher_name}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span>Assigned to</span>
                  <span>·</span>
                  <span className="text-gray-600 font-medium">{event.metadata.driver_name}</span>
                </div>
              </>
            ) : event.triggered_by_name ? (
              <div className="flex items-center gap-1">
                <span className="capitalize">{event.triggered_by || 'system'}</span>
                <span>·</span>
                <span>{event.triggered_by_name}</span>
              </div>
            ) : event.triggered_by && event.triggered_by !== 'system' ? (
              <div className="flex items-center gap-1">
                <span className="capitalize">{event.triggered_by}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span>System</span>
              </div>
            )}
            <div>{formatTime(event.event_time)}</div>
          </div>
        ) : (
          <p className="text-xs text-gray-300 mt-0.5">Pending</p>
        )}
        {event.notes && <p className="text-xs text-gray-500 mt-1">{event.notes}</p>}
      </div>
    </div>
  );
};

// ==================== Recorded Event Row (for flat/default modes) ====================

const EventRow: React.FC<{
  event: TaskTimelineEvent;
  isLast?: boolean;
}> = ({ event, isLast = false }) => (
  <div className="flex items-start gap-3 relative ml-3">
    {/* Vertical connecting line */}
    {!isLast && (
      <div className="absolute left-[9px] top-7 bottom-0 w-px bg-gray-200" />
    )}

    {/* Status icon */}
    <ProcessingStatusIcon status="completed" size="sm" />

    {/* Content */}
    <div className="flex-1 min-w-0 pb-4">
      <p className="text-sm font-medium text-gray-800">{getEventTitle(event)}</p>
      <div className="mt-0.5 text-xs text-gray-400 space-y-0.5">
        <div className="flex items-center gap-1">
          {getTriggeredByIcon(event)}
          <span className="capitalize">{event.triggered_by || 'system'}</span>
          <span>·</span>
          <span>{getTriggeredByLabel(event)}</span>
        </div>
        <div>{formatTime(event.event_time)}</div>
      </div>
      {event.notes && <p className="text-xs text-gray-500 mt-1">{event.notes}</p>}
    </div>
  </div>
);

// ==================== Template Phase Section ====================

const TemplatePhaseSection: React.FC<{
  phase: TaskPhase;
  data: TaskPhaseTemplateData;
  compact?: boolean;
  defaultExpanded?: boolean;
}> = ({ phase, data, compact = false, defaultExpanded }) => {
  const getInitialExpanded = () => {
    if (defaultExpanded !== undefined) return defaultExpanded;
    if (data.status === 'in_progress') return true;
    if (phase === 'preparation' || phase === 'execution') return true;
    return false;
  };

  const [isExpanded, setIsExpanded] = useState(getInitialExpanded);
  const [showAll, setShowAll] = useState(false);

  if (data.total === 0) return null;

  const isComplete = data.status === 'completed';
  const isInProgress = data.status === 'in_progress';

  // In compact mode, limit events shown per phase
  const visibleEvents = compact && !showAll
    ? data.events.slice(0, COMPACT_PHASE_LIMIT)
    : data.events;
  const hiddenCount = data.events.length - visibleEvents.length;

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2 hover:bg-gray-50/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
          <span
            className={cn(
              'font-medium text-sm',
              isComplete && 'text-emerald-600',
              isInProgress && 'text-blue-600',
              !isComplete && !isInProgress && 'text-gray-400'
            )}
          >
            {data.phase_name}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {isComplete && <Check className="w-3.5 h-3.5 text-emerald-500" />}
          <span
            className={cn(
              'text-xs font-medium',
              isComplete && 'text-emerald-500',
              isInProgress && 'text-blue-500',
              !isComplete && !isInProgress && 'text-gray-400'
            )}
          >
            {data.completed}/{data.total}
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="pb-2 pl-2">
          {visibleEvents.map((event, index) => (
            <TemplateEventRow
              key={event.template_key}
              event={event}
              isLast={index === visibleEvents.length - 1 && hiddenCount === 0}
            />
          ))}
          {hiddenCount > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
              className="ml-3 pl-6 text-xs text-blue-500 hover:text-blue-600 font-medium py-1"
            >
              +{hiddenCount} more
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== Main Component ====================

export const TaskEventsList: React.FC<TaskEventsListProps> = ({
  taskId,
  flat = false,
  compact = false,
  showPhases = false,
  showProgress = false,
  showTimeSummary = true,
  taskType,
  currentStatus,
  onRefresh,
  className,
}) => {
  // Use template-based hook when showPhases=true (except flat mode)
  const useTemplate = showPhases && !flat;

  const timelineResult = useTaskTimeline({ taskId, enabled: !useTemplate });
  const flowResult = useTaskProcessingFlow({ taskId, enabled: useTemplate });

  const loading = useTemplate ? flowResult.loading : timelineResult.loading;
  const error = useTemplate ? flowResult.error : timelineResult.error;
  const refresh = useTemplate ? flowResult.refresh : timelineResult.refresh;

  const handleRefresh = () => {
    refresh();
    onRefresh?.();
  };

  // Auto-refresh on real-time timeline events from WebSocket
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    if (!taskId) return;
    const numericTaskId = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;

    const handler = (data: { taskId?: number }) => {
      if (data?.taskId === numericTaskId) {
        refreshRef.current();
      }
    };

    socketService.on('task_timeline_event', handler);
    return () => {
      socketService.off('task_timeline_event', handler);
    };
  }, [taskId]);

  // Loading state
  if (loading) {
    return (
      <div className={cn(className)}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn(className)}>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
          <p className="text-sm text-gray-500 mb-3">{error}</p>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ==================== Flat mode (for embedding in OrderProcessingFlow) ====================
  if (flat) {
    const data = timelineResult.data;
    if (!data?.events || data.events.length === 0) {
      return (
        <div className={cn(className)}>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">No timeline data</p>
          </div>
        </div>
      );
    }

    const events = [...data.events].reverse();
    return (
      <div className={cn('space-y-0', className)}>
        {events.map((event, index) => (
          <EventRow key={event.id || index} event={event} isLast={index === events.length - 1} />
        ))}
      </div>
    );
  }

  // ==================== Template-based modes (showPhases=true) ====================
  if (useTemplate) {
    const flowData = flowResult.data;

    // Empty/template state: show template even with 0 completed events
    if (!flowData) {
      return (
        <div className={cn(className)}>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">No processing flow data</p>
          </div>
        </div>
      );
    }

    const { phases, completed_count, total_count, time_summary } = flowData;
    const percentage = total_count > 0 ? Math.round((completed_count / total_count) * 100) : 0;

    return (
      <div className={cn(className)}>
        {/* Header */}
        {!compact && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 bg-blue-500 rounded-full" />
              <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Processing Flow
              </h4>
              {taskType && (
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500 font-medium">
                  {taskType}
                </span>
              )}
            </div>
            <button
              onClick={handleRefresh}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Compact header */}
        {compact && (
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Processing Flow
            </h4>
            <button
              onClick={handleRefresh}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Progress bar */}
        {(showProgress || compact) && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-semibold text-gray-700">{percentage}%</span>
              <span className="text-xs text-gray-400">
                {completed_count}/{total_count} events
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Phases */}
        <div>
          {(['preparation', 'execution', 'return', 'completion'] as TaskPhase[]).map((phase) => {
            const phaseData = phases[phase];
            if (!phaseData || phaseData.total === 0) return null;
            return (
              <TemplatePhaseSection
                key={phase}
                phase={phase}
                data={phaseData}
                compact={compact}
              />
            );
          })}
        </div>

        {/* Time summary */}
        {showTimeSummary && time_summary && (
          time_summary.total_terminal_minutes !== undefined ||
          time_summary.total_warehouse_minutes !== undefined
        ) && (
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-400">
            {time_summary.total_terminal_minutes !== undefined && (
              <span className="flex items-center gap-1">
                <Ship className="w-3 h-3" />
                Terminal: {time_summary.total_terminal_minutes}m
              </span>
            )}
            {time_summary.total_warehouse_minutes !== undefined && (
              <span className="flex items-center gap-1">
                <Building2 className="w-3 h-3" />
                Warehouse: {time_summary.total_warehouse_minutes}m
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // ==================== Default mode: flat list with header ====================
  const data = timelineResult.data;

  if (!data?.events || data.events.length === 0) {
    return (
      <div className={cn(className)}>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Clock className="w-8 h-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-400">No timeline data</p>
        </div>
      </div>
    );
  }

  // Compact mode without phases: limited events
  if (compact) {
    // data.events is DESC (newest first). Take latest 5, then reverse to show chronologically.
    const events = [...data.events].slice(0, 5).reverse();
    return (
      <div className={cn(className)}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Timeline
          </h4>
          <button
            onClick={handleRefresh}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
        <div className="space-y-0">
          {events.map((event, index) => (
            <EventRow key={event.id || index} event={event} isLast={index === events.length - 1} />
          ))}
        </div>
        {data.events.length > 5 && (
          <p className="text-center text-xs text-gray-400 mt-2">
            +{data.events.length - 5} more events
          </p>
        )}
      </div>
    );
  }

  // Default: flat list of all events (newest first) with header
  const events = [...data.events].reverse();
  return (
    <div className={cn(className)}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Timeline ({data.events.length} events)
        </h4>
        <button
          onClick={handleRefresh}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>
      <div className="space-y-0">
        {events.map((event, index) => (
          <EventRow key={event.id || index} event={event} isLast={index === events.length - 1} />
        ))}
      </div>
    </div>
  );
};
